import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  StarOff,
  GripVertical,
  Package,
  Search
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchProducts = async () => {
    try {
      const response = await api.get("/admin/products");
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (productId) => {
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const toggleVisibility = async (productId) => {
    try {
      await api.patch(`/admin/products/${productId}/visibility`);
      toast.success("Visibility updated");
      fetchProducts();
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const toggleFeatured = async (productId) => {
    try {
      await api.patch(`/admin/products/${productId}/featured`);
      toast.success("Featured status updated");
      fetchProducts();
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout title="Product Management">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-slate-950/50 border-slate-800 focus:border-primary rounded-lg h-10"
            />
          </div>
          <Link to="/admin/products/new">
            <Button
              data-testid="add-product-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg uppercase tracking-widest text-xs"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Product
            </Button>
          </Link>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-900/50 border border-slate-800 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <Package className="w-16 h-16 text-slate-700 mb-4" />
            <h3 className="text-xl font-bold mb-2">No Products Found</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery ? "No search results" : "No products added yet"}
            </p>
            <Link to="/admin/products/new">
              <Button className="rounded-lg bg-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add First Product
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center gap-4 hover:border-slate-700 transition-colors ${
                  !product.is_visible ? "opacity-60" : ""
                }`}
                data-testid={`product-row-${product.id}`}
              >
                {/* Drag Handle */}
                <div className="drag-handle hidden sm:block">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </div>

                {/* Image */}
                <div className="w-16 h-16 bg-slate-800 rounded-lg flex-shrink-0 overflow-hidden">
                  <img
                    src={product.image_url || "/logo-icon.svg"}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold truncate">{product.name}</h3>
                    {product.is_featured && (
                      <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    )}
                    {!product.is_visible && (
                      <span className="text-xs px-2 py-0.5 bg-slate-800 rounded-full text-muted-foreground">
                        HIDDEN
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {product.short_description}
                  </p>
                  <p className="text-sm text-primary font-bold mt-1">
                    ${product.price.toFixed(2)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleFeatured(product.id)}
                    data-testid={`toggle-featured-${product.id}`}
                    title={product.is_featured ? "Remove from featured" : "Mark as featured"}
                    className="h-8 w-8 rounded-lg"
                  >
                    {product.is_featured ? (
                      <StarOff className="w-4 h-4 text-yellow-500" />
                    ) : (
                      <Star className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleVisibility(product.id)}
                    data-testid={`toggle-visibility-${product.id}`}
                    title={product.is_visible ? "Hide" : "Show"}
                    className="h-8 w-8 rounded-lg"
                  >
                    {product.is_visible ? (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>

                  <Link to={`/admin/products/${product.id}`}>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`edit-product-${product.id}`}
                      title="Edit"
                      className="h-8 w-8 rounded-lg"
                    >
                      <Edit className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </Link>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-product-${product.id}`}
                        title="Delete"
                        className="h-8 w-8 rounded-lg text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-950 border-slate-800 rounded-xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Product</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{product.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(product.id)}
                          className="rounded-lg bg-destructive text-destructive-foreground"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
